import { checkRepairSlug, repairSlugOf } from './check-repair-route';

/** @ticket https://bubo.vretta.com/vea/project-management/vretta-project-notes/eqao/authoring/-/work_items/1938 */

export const ITEM_MATRIX_AUDIT_SLUG = 'ITEM_MATRIX_AUDIT';
export const ITEM_MATRIX_AUTOFIX_SLUG = 'ITEM_MATRIX_AUDIT_IS_AUTOFIXING';
export const ITEM_MATRIX_AUTOFIX_CHECK_IDS = ['UNCONFIRMED', 'OUTDATED'];
/** items with expected answers not validated against the score matrix (authoring#1940) */
export const ITEM_MATRIX_EXPECTED_ANSWER_CHECK_ID = 'FLAGGED_ITEMS';
/** Seeded informative audit that offers the score matrix fill-in as a bulk action (authoring#1948) */
export const SCORE_MATRIX_FILL_IN_AUDIT_SLUG = 'SCORE_MATRIX_FILL_IN_AUDIT';
export const SCORE_MATRIX_FILL_IN_AUTOFIX_SLUG = 'SCORE_MATRIX_FILL_IN_IS_AUTOFIXING';
/** library audits may name this one; it runs the same score matrix fill-in */
export const SCORE_MATRIX_AUDIT_AUTOFIX_SLUG = 'SCORE_MATRIX_AUDIT_IS_AUTOFIXING';
export const SCORE_MATRIX_AUDIT_REPAIR_SLUGS = [ITEM_MATRIX_AUTOFIX_SLUG, SCORE_MATRIX_FILL_IN_AUTOFIX_SLUG, SCORE_MATRIX_AUDIT_AUTOFIX_SLUG];

/** @ticket https://bubo.vretta.com/vea/project-management/vretta-project-notes/eqao/authoring/-/work_items/1986 */
export const UNINITIALIZED_MATRIX_CHECK_ID = 'UNINITIALIZED_MATRIX';

export type ScoreMatrixRepairMode = 'confirm-missing' | 'reconfirm-unchanged' | 'recheck-answers' | 'initialize-never';

export interface IScoreMatrixCheckRepair {
  mode: ScoreMatrixRepairMode;
  slug: string;
  /** the score matrix check this repair is the default for */
  checkId: string;
  caption: { en: string, fr: string };
}

/** One repair per score matrix check, each doing only what that check found. */
export const SCORE_MATRIX_CHECK_REPAIRS: IScoreMatrixCheckRepair[] = [
  {
    mode: 'confirm-missing',
    slug: 'SCORE_MATRIX_CONFIRM_MISSING_IS_AUTOFIXING',
    checkId: 'UNCONFIRMED',
    caption: { en: 'Fill in and confirm never-confirmed matrices', fr: 'Remplir et confirmer les matrices jamais confirmées' },
  },
  {
    mode: 'reconfirm-unchanged',
    slug: 'SCORE_MATRIX_RECONFIRM_UNCHANGED_IS_AUTOFIXING',
    checkId: 'OUTDATED',
    caption: { en: 'Re-confirm matrices whose answer key is unchanged', fr: 'Reconfirmer les matrices dont le corrigé est inchangé' },
  },
  {
    mode: 'recheck-answers',
    slug: 'SCORE_MATRIX_RECHECK_ANSWERS_IS_AUTOFIXING',
    checkId: ITEM_MATRIX_EXPECTED_ANSWER_CHECK_ID,
    caption: { en: 'Re-check expected answers against the matrix', fr: 'Revérifier les réponses attendues avec la matrice' },
  },
  {
    mode: 'initialize-never',
    slug: 'SCORE_MATRIX_INITIALIZE_IS_AUTOFIXING',
    checkId: UNINITIALIZED_MATRIX_CHECK_ID,
    caption: { en: 'Initialize score matrices from the answer key', fr: 'Initialiser les matrices de notation à partir du corrigé' },
  },
];

export const scoreMatrixCheckRepairBySlug = (slug: string) => SCORE_MATRIX_CHECK_REPAIRS.find(repair => repair.slug === slug);

const isScoreMatrixAuditRepair = (audit: { autoFixes?: any, isCompleteFix?: boolean }) =>
  !audit?.isCompleteFix && SCORE_MATRIX_AUDIT_REPAIR_SLUGS.includes(repairSlugOf(audit?.autoFixes));

/** The DB audit catalog has no auto-fix for this audit; attach the built-in one unless the catalog defines its own */
export const withScoreMatrixAutoFix = <T extends { slug: string, autoFixes?: any, isCompleteFix?: boolean, checks?: any[] }>(audits: T[]): T[] => {
  const audit = audits?.find(a => a.slug === ITEM_MATRIX_AUDIT_SLUG);
  if (audit && !audit.autoFixes) {
    audit.autoFixes = {
      slug: ITEM_MATRIX_AUTOFIX_SLUG,
      caption: { en: 'Re-confirm unchanged matrices', fr: 'Reconfirmer les matrices inchangées' },
    };
    audit.isCompleteFix = false;
    // "Can be auto-repaired" set on any check in the Audit Library decides where the wrench shows; an explicit off stays off
    if (!audit.checks?.some(check => check?.autoFix === true && check.id !== ITEM_MATRIX_EXPECTED_ANSWER_CHECK_ID)) {
      audit.checks?.forEach(check => {
        if (ITEM_MATRIX_AUTOFIX_CHECK_IDS.includes(check.id) && check.autoFix === undefined) {
          check.autoFix = true;
        }
      });
    }
  }
  // each score matrix check runs its own repair unless the check names one, or stores null for the audit's
  (audits ?? []).filter(isScoreMatrixAuditRepair).forEach(matrixAudit => {
    matrixAudit.checks?.forEach(check => {
      const own = SCORE_MATRIX_CHECK_REPAIRS.find(repair => repair.checkId === check?.id);
      if (own && check.autoFixes === undefined) {
        check.autoFixes = { slug: own.slug, caption: { ...own.caption } };
      }
    });
  });
  return audits;
};

/**
 * Checks whose flagged items the score matrix fill-in starts from: the check whose wrench was pressed,
 * otherwise every check marked "Can be auto-repaired" (expected-answer flags are handled separately).
 */
export const getScoreMatrixFixSourceCheckIds = (
  checks: { id?: string, autoFix?: boolean }[] | undefined,
  pressedCheckId?: string | null,
): string[] => {
  if (pressedCheckId) {
    return [pressedCheckId];
  }
  const ids = (checks ?? [])
    .filter(check => check?.autoFix && check.id && check.id !== ITEM_MATRIX_EXPECTED_ANSWER_CHECK_ID)
    .map(check => check.id);
  return ids.length ? ids : ITEM_MATRIX_AUTOFIX_CHECK_IDS;
};

export interface IScoreMatrixFixSources {
  /** checks whose items get their matrices planned */
  matrixCheckIds: string[];
  /** checks whose items get their expected answers re-checked */
  answerCheckIds: string[];
  /** "Also confirm never-confirmed matrices" starts ticked */
  presetUnconfirmed: boolean;
}

/**
 * Where one score matrix repair run reads its items. A check-specific repair only reads the checks that
 * run it (the pressed one, or `runCheckIds` for a per-item run); the audit-wide repair keeps its old reach.
 */
export const getScoreMatrixFixSources = (
  audit: { autoFixes?: any, checks?: { id?: string, autoFix?: boolean, autoFixes?: any }[] } | null | undefined,
  patchSlug: string,
  pressedCheckId?: string | null,
  runCheckIds?: string[] | null,
): IScoreMatrixFixSources => {
  const checks = audit?.checks ?? [];
  const mode = scoreMatrixCheckRepairBySlug(patchSlug);
  if (!mode) {
    const inRun = runCheckIds ? checks.filter(check => runCheckIds.includes(check?.id)) : checks;
    return {
      matrixCheckIds: getScoreMatrixFixSourceCheckIds(inRun, pressedCheckId),
      answerCheckIds: [ITEM_MATRIX_EXPECTED_ANSWER_CHECK_ID],
      presetUnconfirmed: !!pressedCheckId && pressedCheckId !== 'OUTDATED',
    };
  }
  let ids = pressedCheckId ? [pressedCheckId] : (runCheckIds ?? checks
    .filter(check => check?.autoFix === true && check.id && checkRepairSlug(audit, check.id) === patchSlug)
    .map(check => check.id));
  if (!ids.length) {
    ids = [mode.checkId];
  }
  if (mode.mode === 'recheck-answers') {
    return { matrixCheckIds: [], answerCheckIds: ids, presetUnconfirmed: false };
  }
  if (mode.mode === 'initialize-never') {
    // also read UNCONFIRMED: items initialized earlier but never confirmed can be confirmed in the same run (second stage)
    const withUnconfirmed = ids.includes('UNCONFIRMED') ? ids : [...ids, 'UNCONFIRMED'];
    return { matrixCheckIds: withUnconfirmed, answerCheckIds: [], presetUnconfirmed: true };
  }
  return { matrixCheckIds: ids, answerCheckIds: [], presetUnconfirmed: mode.mode === 'confirm-missing' };
};
